import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Users, Send } from "lucide-react";

const Toggle = ({ title, hint, checked, onChange, testid }) => (
  <div className="flex items-center justify-between gap-6 py-4">
    <div>
      <div className="text-sm font-medium">{title}</div>
      <p className="text-xs text-muted-foreground mt-1">{hint}</p>
    </div>
    <Switch checked={checked} onCheckedChange={onChange} data-testid={testid} />
  </div>
);

export default function SmsCleanerDigest({ overview, onChange }) {
  const [running, setRunning] = useState(false);

  const setFlag = async (key, v) => {
    try {
      await api.put("/settings/sms", { sms_enabled: overview.sms_enabled, [key]: v });
      toast.success("Saved");
      onChange();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const runNow = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/sms/cleaner-digest/run");
      toast.success(data.count ? `${data.count} cleaner text${data.count > 1 ? "s" : ""} processed — see history` : "No cleaners have bookings tomorrow");
      onChange();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setRunning(false);
    }
  };

  return (
    <section className="border border-border/60 bg-card p-4 sm:p-8 rounded-sm" data-testid="sms-cleaner-digest">
      <div className="flex items-center gap-2 font-medium"><Users className="w-4 h-4 text-brand" /> Cleaner texts</div>
      <p className="text-xs text-muted-foreground mt-1">Cleaners can switch each of these off for themselves under "My notifications".</p>
      <div className="divide-y divide-border/60 mt-2">
        <Toggle title={`Evening job list · ${overview.cleaner_digest_time}`} hint="Tomorrow's bookings with times and addresses (Brisbane time)."
          checked={overview.cleaner_digest_enabled} onChange={(v) => setFlag("cleaner_digest_enabled", v)} testid="cleaner-digest-switch" />
        <Toggle title="Instant change alerts" hint="When a cleaner is added, removed, or a booking moves or is cancelled today or tomorrow."
          checked={overview.cleaner_alerts_enabled} onChange={(v) => setFlag("cleaner_alerts_enabled", v)} testid="cleaner-alerts-switch" />
        <Toggle title="Job completed alerts" hint="Text the business phone when a cleaner taps Mark complete."
          checked={overview.completion_alerts_enabled} onChange={(v) => setFlag("completion_alerts_enabled", v)} testid="completion-alerts-switch" />
      </div>
      <Button variant="outline" onClick={runNow} disabled={running} data-testid="cleaner-digest-run" className="rounded-sm mt-4 gap-2">
        <Send className="w-4 h-4" /> {running ? "Sending…" : "Send tomorrow's job texts now"}
      </Button>
      <p className="text-xs text-muted-foreground mt-3">Uses each team member's phone number from the Team page.</p>
    </section>
  );
}
